import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { Summary as SummaryType } from "@/types/summary";
import summaryData from "@/routes/innovation-days-demo/data/little_prince_summary.json";

interface SummaryProps {
  onSentenceClick?: (sentenceIndex: number) => void;
  enableClaimsNavigation?: boolean;
}

export function Summary({
  onSentenceClick,
  enableClaimsNavigation = true,
}: SummaryProps) {
  const summary = summaryData as SummaryType;
  const navigate = useNavigate();

  // Extract refined sentences organized by paragraphs using refinement data
  const { sentences, paragraphs } = useMemo(() => {
    if (!summary.refinement?.chunk_refinements) {
      return { sentences: [], paragraphs: [] };
    }

    const allSentences: Array<{
      text: string;
      index: number;
      paragraphIndex: number;
    }> = [];
    const allParagraphs: Array<{ sentenceIndices: number[] }> = [];
    let globalIndex = 0;
    let currentParagraphIndex = 0;

    // Process each chunk refinement
    summary.refinement.chunk_refinements.forEach((chunkRefinement) => {
      const chunkSentences = chunkRefinement.sentence_refinements.map(
        (sr) => sr.refined_sentence,
      );
      const lineBreaks = chunkRefinement.line_break_indices;

      let currentParagraph: number[] = [];

      // Process sentences in this chunk
      chunkSentences.forEach((sentence, localIndex) => {
        // Add sentence to global list
        allSentences.push({
          text: sentence,
          index: globalIndex,
          paragraphIndex: currentParagraphIndex,
        });
        currentParagraph.push(globalIndex);
        globalIndex += 1;

        // Check if we need a paragraph break after this sentence
        if (lineBreaks.includes(localIndex)) {
          allParagraphs.push({ sentenceIndices: currentParagraph });
          currentParagraph = [];
          currentParagraphIndex += 1;
        }
      });

      // Add any remaining sentences as the last paragraph of this chunk
      if (currentParagraph.length > 0) {
        allParagraphs.push({ sentenceIndices: currentParagraph });
        currentParagraph = [];
        currentParagraphIndex += 1;
      }
    });

    return { sentences: allSentences, paragraphs: allParagraphs };
  }, [summary.refinement]);

  const handleSentenceClick = (sentenceIndex: number) => {
    if (enableClaimsNavigation) {
      // Navigate to manuscript with claims mode
      navigate({
        to: "/innovation-days-demo/viewer",
        search: {
          annotationType: "claims",
          sentenceIndex,
        },
      });
    } else if (onSentenceClick) {
      onSentenceClick(sentenceIndex);
    }
  };

  return (
    <div className="min-h-screen pb-8 text-sm">
      {/* Main Content */}
      <div className="mx-auto">
        <div className="bg-white rounded-3xl shadow-sm p-6">
          {/* Render refined sentences grouped by paragraph */}
          {paragraphs.map((paragraph) => (
            <div
              key={`paragraph-${paragraph.sentenceIndices[0]}`}
              className="mb-4"
            >
              <p
                className="text-[15px] leading-[24px] text-justify"
                style={{
                  color: "#1E293B",
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}
              >
                {paragraph.sentenceIndices.map(
                  (sentenceIndex, indexInParagraph) => {
                    const sentence = sentences[sentenceIndex];

                    return (
                      <span
                        key={`sentence-${sentenceIndex}`}
                        onClick={() => handleSentenceClick(sentenceIndex)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSentenceClick(sentenceIndex);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer hover:bg-primary-50 transition-colors duration-150"
                      >
                        {sentence.text}
                        {indexInParagraph < paragraph.sentenceIndices.length - 1
                          ? " "
                          : ""}
                      </span>
                    );
                  },
                )}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/innovation-days-demo/_layout/summary")({
  component: Summary,
});
